import { useMemo } from 'react';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useRemoteFeatureFlag } from '@shared/core/featureFlags/remoteFeatureFlags';
import { addError } from '@shared/utils/logger';

/**
 * A sanitization rule for transforming problematic characters using regex.
 */
export interface SanitizationRule
  extends Readonly<{
    /** Regex pattern string, including delimiters and flags, e.g. "/'/g" */
    search: string;
    /** Replacement string */
    replace: string;
  }> {}

export type SanitizationMap = ReadonlyArray<SanitizationRule>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isValidSanitizationRule = (rule: any): boolean => {
  return (
    rule &&
    !Array.isArray(rule) &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    typeof (rule as any).search === 'string' &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    typeof (rule as any).replace === 'string'
  );
};

const isValidSanitizationMap = (payload: unknown): boolean => {
  return Array.isArray(payload) && payload.every(isValidSanitizationRule);
};

/**
 * Hook that provides a sanitization function to clean strings
 * using configurable regex rules from a remote feature flag (`nuiteeQueryStringSanitization`).
 *
 * @returns An object containing:
 * - `sanitizeString(input: string): string` — sanitized string
 * - `loading: boolean` — whether the feature flag is still loading
 */
export const useSanitizedNuiteeQueryString = () => {
  const { payload, loading } = useRemoteFeatureFlag('nuiteeQueryStringSanitization');

  const compiledRules = useMemo(() => {
    const rawRules = isValidSanitizationMap(payload) ? (payload as SanitizationMap) : [];

    return rawRules
      .map(rule => {
        try {
          const match = rule.search.match(/^\/(.+)\/([gims]*)$/);
          if (!match) return null;

          const [, pattern, flags] = match;
          return {
            regex: new RegExp(pattern, flags),
            replace: rule.replace,
          };
        } catch (error) {
          addError(error, {
            context: 'useSanitizedString:compileRule',
            rule,
          });
          return null;
        }
      })
      .filter(Boolean) as Array<{ regex: RegExp; replace: string }>;
  }, [payload]);

  /**
   * Sanitizes the input string using regex rules.
   * Falls back to original string if rules are invalid.
   */
  const sanitizeNuiteeQueryString = useEventCallback((input: string): string => {
    return compiledRules.reduce((acc, { regex, replace }) => {
      return acc.replace(regex, replace);
    }, input);
  });

  return { sanitizeNuiteeQueryString, loading };
};
